import { keyBy } from "lodash";
import { FC, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useHubRideActionConfirm,
  useModalConfirmsRide,
  useRidesProcessing,
} from "@ryde/redux";
import {
  dateToComparableNumber,
  errorAndToastSimpleResult,
  errors,
  getRidePassengerDisplayStationId,
  HubRideActionKind,
  IRideDto,
  Log,
  PassengerDeleteResultEnum,
  RideStationPanelMode,
  unique,
} from "@ryde/shared";

import { KanbanItemPassengersGridWrapper } from "../rides.kanban.item.styled";
import { KanbanPassengersRow } from "./rides.kanban.item.passengers.row";

interface IKanbanItemPassengers {
  rideDto: IRideDto;
}

export const KanbanItemPassengers: FC<IKanbanItemPassengers> = memo(
  ({ rideDto }): JSX.Element => {
    const { t } = useTranslation();
    const passengers = useMemo(
      () => rideDto?.metadata?.passengersInfo || [],
      [rideDto?.metadata?.passengersInfo],
    );
    const { removePassengersFromRide } = useRidesProcessing();
    const [selectedPassIds, setSelectedPassIds] = useState<number[]>([]);
    const { confirmRidePassengerDeleteBookingAction } = useModalConfirmsRide();
    const { confirmHubRideAction } = useHubRideActionConfirm();

    const removePassengerFromRide = useEvent(
      async (
        e: React.MouseEvent,
        hoverPassId?: number | undefined,
      ): Promise<void> => {
        const passengersIdsToRemove = hoverPassId
          ? unique([...selectedPassIds, hoverPassId])
          : unique([...selectedPassIds]);

        const passengersIdsToRemoveSet = new Set(passengersIdsToRemove);
        const passengersToRemove = passengers.filter(p =>
          passengersIdsToRemoveSet.has(p.id),
        );

        const isHubModifyConfirmed = await confirmHubRideAction({
          rides: [rideDto],
          actionKind: HubRideActionKind.Modify,
        });

        if (!isHubModifyConfirmed) {
          return;
        }

        const deletePassengerBookingAction =
          await confirmRidePassengerDeleteBookingAction({
            passengers: passengersToRemove,
            mode: RideStationPanelMode.GRID_PANEL,
            ride: rideDto,
          });

        if (
          deletePassengerBookingAction === PassengerDeleteResultEnum.NoAction
        ) {
          Log.warnAndToast(
            t(errors.ride.removePassengersFromRideCanceledByUser),
            passengersIdsToRemove,
          );

          return;
        }

        const res = await removePassengersFromRide?.(
          rideDto?.id || 0,
          passengersIdsToRemove,
          deletePassengerBookingAction,
        );

        if (res.success) {
          setSelectedPassIds([]);
        } else {
          errorAndToastSimpleResult(res, t);
        }
      },
    );

    const selectedPassIdsSet = useMemo(
      () => new Set(selectedPassIds),
      [selectedPassIds],
    );

    const setPassengerSelected = useCallback(
      (id: number) => () => {
        if (!selectedPassIdsSet.has(id)) {
          setSelectedPassIds([...selectedPassIds, id]);
        } else {
          setSelectedPassIds(selectedPassIds.filter(x => x !== id));
        }
      },
      [selectedPassIds, selectedPassIdsSet],
    );

    const stationInfoGroupedById = useMemo(() => {
      return keyBy(rideDto?.metadata?.stationsInfo, "id");
    }, [rideDto?.metadata?.stationsInfo]);

    const stationIdsOrderIndexMap = useMemo(() => {
      return (
        rideDto?.metadata?.stationIds?.reduce((acc, stationId, orderIndex) => {
          acc.set(stationId, orderIndex);
          return acc;
        }, new Map<number, number>()) ?? new Map<number, number>()
      );
    }, [rideDto?.metadata?.stationIds]);
    const sortedPassenger = useMemo(() => {
      return passengers.toSorted((passenger1, passenger2) => {
        const stationId1 = getRidePassengerDisplayStationId(
          passenger1,
          rideDto.direction,
        );
        const stationId2 = getRidePassengerDisplayStationId(
          passenger2,
          rideDto.direction,
        );

        if (!stationId1 || !stationId2) {
          return 0;
        }

        const arrivalTime1 = stationInfoGroupedById[stationId1]?.arrivalTime;
        const arrivalTime2 = stationInfoGroupedById[stationId2]?.arrivalTime;

        return (
          dateToComparableNumber(arrivalTime1) -
          dateToComparableNumber(arrivalTime2)
        );
      });
    }, [passengers, rideDto.direction, stationInfoGroupedById]);

    return (
      <KanbanItemPassengersGridWrapper className="thin_scrollbar">
        {sortedPassenger?.map((passenger, i) => {
          return (
            <KanbanPassengersRow
              key={`${passenger.firstName} ${passenger.lastName} ${i}`}
              deletePassengerHandler={removePassengerFromRide}
              chosen={selectedPassIdsSet.has(passenger.id)}
              setChosenToggle={setPassengerSelected(passenger.id)}
              passenger={passenger}
              rideDto={rideDto}
              dragSelectedIds={selectedPassIds}
              stationIdsOrderIndexMap={stationIdsOrderIndexMap}
            />
          );
        })}
      </KanbanItemPassengersGridWrapper>
    );
  },
);
